import React, { useLayoutEffect, useRef, useState } from 'react';
import { AppointmentEventType, EventType, TimeRowEventType, ViewType } from "./SchedulingTypes";
import { saveData } from 'services/api/saveData';
import { convertDayKeyAndTimeSlotToEndDateTimeString, convertDayKeyAndTimeSlotToStartDateTimeString } from './functions/schedulingDateUtils';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { useGlobalContext } from 'GlobalContext';
import { useTranslation } from 'react-i18next';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';

export interface CopiedEvent {
    type: 'appointment' | 'activity';
    data: any;
    lastUsedAt: number;
}

export interface RightClickMenuType {
    type: 'event' | 'empty';
    x: number;
    y: number;
    event?: EventType;
    resourceId?: string | number | null;
    userId?: number | null;
    dayKey?: string;
    timeSlot?: string;
}

interface RightClickMenuProps {
    viewType: ViewType;
    menu: RightClickMenuType;
    copiedEvent: CopiedEvent | null;
    userLocale: string;
    userTimezone: string;
    onCopy: (event: EventType) => void;
    onClose: () => void;
    onRefresh: () => void;
    setCopiedEvent: React.Dispatch<React.SetStateAction<CopiedEvent | null>>;
    onShowOnMap?: (event: AppointmentEventType | TimeRowEventType) => void;
    isEquipmentResource?: (resourceId: string | number) => boolean;
}

// Renders the dropdown menu on the mouse position, flipped to the other
// side of the mouse when it would overflow the edge of the screen
const RightClickMenuContainer: React.FC<{ x: number; y: number; children: React.ReactNode }> = ({ x, y, children }) => {
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    // Measure the rendered menu to keep it inside the viewport
    useLayoutEffect(() => {
        if (!menuRef.current) return;

        // Get the menu rect
        const rect = menuRef.current.getBoundingClientRect();

        // Start the menu on the mouse position
        let left = x;
        let top = y;

        // Show the menu above the mouse when it overflows the bottom of the screen
        if (top + rect.height > window.innerHeight) {
            top = y - rect.height;
        }

        // Show the menu on the left side of the mouse when it overflows the right of the screen
        if (left + rect.width > window.innerWidth) {
            left = x - rect.width;
        }

        // Clamp the position to the top left of the screen
        if (top < 0) top = 0;
        if (left < 0) left = 0;

        setPosition({ top, left });
    }, [x, y]);

    return (
        <div
            ref={menuRef}
            style={{
                position: 'fixed',
                top: position ? position.top : y,
                left: position ? position.left : x,
                visibility: position ? 'visible' : 'hidden',
            }}
            className='dropdown-button-menu'
            onClick={(e) => e.stopPropagation()}
        >
            {children}
        </div>
    );
};

const RightClickMenu: React.FC<RightClickMenuProps> = ({
    viewType,
    menu,
    copiedEvent,
    userLocale,
    userTimezone,
    onCopy,
    onClose,
    onRefresh,
    setCopiedEvent,
    isEquipmentResource,
    onShowOnMap
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { checkDayPasses, handleDayPassError } = useSubcontractorDayPassCheck();

    // Determine wheter the copy event is still valid, it expires after 20 seconds
    const isCopiedEventValid = copiedEvent && (Date.now() - copiedEvent.lastUsedAt) < 20000;

    // Handle the deletion of an event
    const handleDeleteEvent = async (event: EventType) => {
        if (event.type === 'appointment') {
            await saveData({
                apiUrl: `patch_appointment/${event.appointmentId}`,
                method: 'patch',
                data: { deleted: true }
            });
        }

        // A recurring occurrence has no row of its own, stop the series from this date onwards instead
        if (event.type === 'activity' && event.recurrenceId) {
            await saveData({
                apiUrl: `delete_activity_recurrence/${event.recurrenceId}`,
                method: 'patch',
                data: { effective_from: event.occurrenceDate }
            });
        } else if (event.type === 'activity') {
            await saveData({
                apiUrl: `patch_activity/${event.activityId}`,
                method: 'patch',
                data: { deleted: true }
            });
        }

        if (event.type === 'timerow') {
            await saveData({
                apiUrl: `patch_timerow/${event.timerowId}`,
                method: 'patch',
                data: { deleted: true }
            });
        }

        // Close the right click menu
        onClose();

        // Show success alert
        setFloatingAlert({
            type: 'success',
            message: `scheduling.scheduling_board.right_click_${event.type}_deleted`
        });

        // Refresh the events
        onRefresh();
    };

    // Handle the locking or unlocking of an appointment event
    const handleToggleLockEvent = async (event: EventType) => {
        if (event.type !== 'appointment') return;

        // Determine the new locked state
        const newLockedState = !event.locked;

        // Save the new locked state of the appointment
        await saveData({
            apiUrl: `patch_appointment/${event.appointmentId}`,
            method: 'patch',
            data: { locked: newLockedState }
        });

        // Close the right click menu
        onClose();

        // Show success alert
        setFloatingAlert({
            type: 'success',
            message: newLockedState
                ? 'scheduling.scheduling_board.right_click_appointment_locked'
                : 'scheduling.scheduling_board.right_click_appointment_unlocked'
        });

        // Refresh the events
        onRefresh();
    };

    // Handle the pasting of a copied event
    const handlePasteEvent = async () => {

        // Unpack the menu props
        const { resourceId, userId, dayKey, timeSlot } = menu;

        // Return if copied event and daykey are missing
        if (!copiedEvent || !dayKey) return;

        // Prevent pasting the event on the same day and resource
        if ((resourceId 
            && copiedEvent.data.resourceId === String(resourceId) 
            && copiedEvent.data.dayKey === String(dayKey)) 
        ||
            (userId 
            && copiedEvent.data.userId === userId
            && copiedEvent.data.dayKey === String(dayKey))
        ) {
            setFloatingAlert({
                type: 'warning',
                message: 'scheduling.scheduling_board.cannot_copy_same_spot'
            });

            onClose();
            return;
        }

        // Prevent pasting an activity on the unassigned row
        if (resourceId && copiedEvent.type === 'activity' && menu.resourceId === 'unassigned') {
            setFloatingAlert({
                type: 'warning',
                message: 'scheduling.scheduling_board.cannot_copy_activity_unassigned'
            });

            onClose();
            return;
        }

        // Prevent pasting an activity on an equipment row, an activity always needs an employee
        if (resourceId && copiedEvent.type === 'activity' && isEquipmentResource && isEquipmentResource(resourceId)) {
            setFloatingAlert({
                type: 'warning',
                message: 'scheduling.scheduling_board.cannot_copy_activity_equipment'
            });

            onClose();
            return;
        }
    
        // Determine wheter the copied event is still valid. It expires after 20 seconds automatically
        const isValid = (Date.now() - copiedEvent.lastUsedAt) < 20000;
        if (!isValid) return;
    
        // For the personal calendar, use the timeslot of the paste position
        let startTimeValue;
        if (viewType === 'personal_calendar') {
            startTimeValue = timeSlot;

        // Otherwise use the start time of the original event
        } else {
            startTimeValue = copiedEvent.data.startTime;
        }
    
        // Generate start and end datetimes
        const startDateTime = convertDayKeyAndTimeSlotToStartDateTimeString(dayKey, startTimeValue, userLocale, userTimezone);
        const endDateTime = convertDayKeyAndTimeSlotToEndDateTimeString(dayKey, startTimeValue, copiedEvent.data.durationMinutes, userLocale, userTimezone);
    
        // Convert the start and end datetimes to utc
        const utcStart = convertLocalDateTimeToUTC(startDateTime, userLocale, userTimezone);
        const utcEnd = convertLocalDateTimeToUTC(endDateTime, userLocale, userTimezone);

        // For the personal calendar, the target is the user id
        // for the employee scheduler, the target is the resource id
        let target;
        if (resourceId) target = { resource: resourceId === 'unassigned' ? null : resourceId };
        if (userId) target = { user: userId }
    
        // Post copied appointment
        if (copiedEvent.type === 'appointment') {

            // Warn and stop when the target subcontractor has no day pass for this day
            const hasTargetResource = resourceId && resourceId !== 'unassigned';
            if (hasTargetResource || userId) {
                const mayContinue = await checkDayPasses([{
                    ...(hasTargetResource ? { resource_id: resourceId } : { user_id: userId }),
                    start_datetime: utcStart,
                    end_datetime: utcEnd,
                    timezone: userTimezone
                }]);
                if (!mayContinue) {
                    onClose();
                    return;
                }
            }

            // Save the pasted appointment, a refusal for missing day passes shows the modal
            try {
                await saveData({
                    apiUrl: 'paste_appointment',
                    method: 'post',
                    data: {
                        appointment_id: copiedEvent.data.appointmentId,
                        ...target,
                        start_datetime: utcStart,
                        end_datetime: utcEnd
                    }
                });
            } catch (error) {
                onClose();
                if (handleDayPassError(error)) return;
                throw error;
            }
        }
    
        // Post copied activity
        if (copiedEvent.type === 'activity') {
            await saveData({
                apiUrl: 'paste_activity',
                method: 'post',
                data: {
                    activity_id: copiedEvent.data.activityId,
                    ...target,
                    start_datetime: utcStart,
                    end_datetime: utcEnd
                }
            });
        }

        // Show success alert
        setFloatingAlert({
            type: 'success',
            message: `scheduling.scheduling_board.right_click_${copiedEvent.type}_copied`
        });
    
        // Update the last used time
        setCopiedEvent((prev: CopiedEvent | null) => prev ? { ...prev, lastUsedAt: Date.now() } : null);

        // Refresh the events
        onRefresh();

        // Close the right click menu
        onClose();
    };

    // Render copy/lock/delete menu
    if (menu.type === 'event' && menu.event) {
        return (
            <RightClickMenuContainer x={menu.x} y={menu.y}>
                {/* Copying is not supported for timerow events, or a virtual occurrence of a recurring activity */}
                {menu.event.type !== 'timerow' && !(menu.event.type === 'activity' && menu.event.recurrenceId) && (
                    <div
                        className='dropdown-button-item'
                        onClick={() => {
                            onCopy(menu.event!);
                            onClose();
                        }}
                    >
                        {t('scheduling.scheduling_board.right_click_copy')}
                    </div>
                )}

                {/* Only appointments can be locked to prevent unwanted dragging */}
                {menu.event.type === 'appointment' && (
                    <div
                        className='dropdown-button-item'
                        onClick={() => handleToggleLockEvent(menu.event!)}
                    >
                        {menu.event.locked
                            ? t('scheduling.scheduling_board.right_click_unlock')
                            : t('scheduling.scheduling_board.right_click_lock')}
                    </div>
                )}

                {/* Only appointments and timerows have a job with a location, other activities don't */}
                {(menu.event.type === 'appointment' || menu.event.type === 'timerow') && onShowOnMap && (
                    <div
                        className='dropdown-button-item'
                        onClick={() => {
                            onShowOnMap(menu.event as AppointmentEventType | TimeRowEventType);
                            onClose();
                        }}
                    >
                        {t('scheduling.scheduling_board.right_click_show_on_map')}
                    </div>
                )}

                <div
                    className='dropdown-button-item danger'
                    onClick={() => handleDeleteEvent(menu.event!)}
                >
                    {t('scheduling.scheduling_board.right_click_delete')}
                </div>
            </RightClickMenuContainer>
        );
    };

    // Render paste menu if copied event is valid
    if (menu.type === 'empty' && copiedEvent && isCopiedEventValid) {
        return (
            <RightClickMenuContainer x={menu.x} y={menu.y}>
                <div
                    className='dropdown-button-item'
                    onClick={handlePasteEvent}
                >
                    {t('scheduling.scheduling_board.right_click_paste')}
                </div>
            </RightClickMenuContainer>
        );
    };

    return null;
};

export default RightClickMenu;